import {
  ArrowLeftOutlined,
  ArrowRightOutlined
} from "@ant-design/icons";
import { Card, Empty, Spin } from "antd";
import SaudiRiyal from "assets/svg/saudi_riyal_symbol.svg";
import useAuthHook from "hooks/useAuthHook";
import { useEffect, useMemo, useState } from "react";
import Chart from 'react-apexcharts';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetInstlmntDashboardMutation
} from "services/auth";
import routes, { appRoute } from "shared/constants/routes";
import formatDateDDMMYYYY from "utilities/formatDate";
import { useTranslation } from "../../../contexts/TranslationContext";

const InstlmntDashboard = () => {
  const { translate, language } = useTranslation();
  const navigate = useNavigate();
  const { getUser } = useAuthHook();
  const isRtl = language.dir === "rtl";
  const [getInstlmntDashboardData] = useGetInstlmntDashboardMutation();
  const [installmentData, setInstallmentData] = useState([]);
  const [paidAmount, setPaidAmount] = useState(0);
  const [unpaidAmount, setUnpaidAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [appResults, setAppResults] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState(null);

  const navigateBack = () => {
    if (appResults.length > 1 && selectedAppId) {
      setSelectedAppId(null);
    } else {
      navigate(appRoute(routes.SETTING_ROOT));
    }
  };

  // Memoized chart data updates when amounts change
  const chartData = useMemo(() => ({
    series: [paidAmount, unpaidAmount],
    options: {
      chart: {
        type: 'donut',
        height: 220,
      },
      labels: ['Paid', 'Not paid'],
      colors: ['#22C55E', '#EF4444'],
      legend: {
        show: false
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontWeight: 600,
                color: '#374151',
                offsetY: -10,
                formatter: () => 'Total'
              },
              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 700,
                color: '#111827',
                offsetY: 10,
                formatter: () => totalAmount.toLocaleString()
              },
              total: {
                show: true,
                showAlways: true,
                label: 'Total',
                fontSize: '16px',
                fontWeight: 600,
                color: '#374151',
                formatter: () => totalAmount.toLocaleString()
              }
            }
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 350,
            height: 250
          }
        }
      }]
    }
  }), [paidAmount, unpaidAmount, totalAmount]);

  // extract chart & table data for one app
  const processAppData = (app) => {
    const formatted = app.result.map(item => ({
      installmentNumber: item.INSTLMNT_NO,
      dueDate: formatDateDDMMYYYY(item.DUE_DATE),
      installmentAmount: item.INSTLMNT_AMT,
      status: item.PAID_FLAG === 'Y' ? 'Paid' : 'Not paid'
    }));
    const sums = app.result.reduce((acc, i) => {
      acc[i.PAID_FLAG === 'Y' ? 'paid' : 'unpaid'] += i.INSTLMNT_AMT; return acc;
    }, { paid: 0, unpaid: 0 });
    const total = sums.paid + sums.unpaid;
    setInstallmentData(formatted);
    setPaidAmount(sums.paid);
    setUnpaidAmount(sums.unpaid);
    setTotalAmount(total);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const user = getUser();
        if (!user) return;
        const res = await getInstlmntDashboardData({
          id: Number(user.IDB_USR_ID),
        }).unwrap();

        if (!res.success) {
          throw new Error(res.message || "Failed to fetch installment dashboard data");
        }

        const result = res?.result || [];
        setAppResults(result);
        if (result.length === 1) {
          processAppData(result[0]);
        } else {
          setInstallmentData([]); setPaidAmount(0); setUnpaidAmount(0); setTotalAmount(0);
        }

        // format table rows
        const formattedData = result.flatMap(app =>
          app.result.map(item => ({
            installmentNumber: item.INSTLMNT_NO,
            dueDate: formatDateDDMMYYYY(item.DUE_DATE),
            installmentAmount: item.INSTLMNT_AMT,
            status: item.PAID_FLAG === 'Y' ? 'Paid' : 'Not paid'
          }))
        );

        // compute sums
        const chartDataSource = result.flatMap(app =>
          app.result.map(item => ({ amount: item.INSTLMNT_AMT, status: item.PAID_FLAG }))
        );
        const paid = chartDataSource.filter(i => i.status === 'Y').reduce((sum, i) => sum + i.amount, 0);
        const unpaid = chartDataSource.filter(i => i.status !== 'Y').reduce((sum, i) => sum + i.amount, 0);
        const total = paid + unpaid;

        setInstallmentData(formattedData);
        setPaidAmount(paid);
        setUnpaidAmount(unpaid);
        setTotalAmount(total);
      } catch (err) {
        console.error(err);
        toast.error(translate('SETTINGS.NO_SIGNED_APPLICATIONS_FOUND') || "No Signed Applications found");
        navigate(appRoute(routes.SETTING_ROOT));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper to check if selected app (or only app) has no installment data
  const noInstallmentData =
    (appResults.length === 1 && appResults[0]?.result?.length === 0) ||
    (appResults.length > 1 && selectedAppId && appResults.find(a => a.appId === selectedAppId)?.result?.length === 0);

  return (
    <>
      {
        isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Spin size="large" />
          </div>
        ) : appResults.length > 1 && !selectedAppId ? (
          <>
            <div className="gap-3 flex items-center mb-5">
              <div
                className="bg-[#F3EDDD] rounded-lg hover:bg-gray-100 transition duration-300 flex items-center justify-center w-10 h-10"
                onClick={navigateBack}
              >
                {isRtl ? (
                  <ArrowRightOutlined className="text-black text-lg font-extrabold" />
                ) : (
                  <ArrowLeftOutlined className="text-black text-lg font-extrabold" />
                )}
              </div>
              <div className="text-[#161616] text-[22px] font-normal font-sans">
                {translate("SETTINGS.SETTINGS")}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {appResults.map(app => {
                // compute per‐app chart
                const paid = app.result.filter(i => i.status === 'Y').reduce((s, i) => s + i.amount, 0);
                const unpaid = app.result.filter(i => i.status !== 'Y').reduce((s, i) => s + i.amount, 0);
                return (
                  <Card
                    key={app.appId}
                    hoverable
                    onClick={() => { setSelectedAppId(app.appId); processAppData(app); }}
                  >
                    <div className="mb-2 font-semibold">App #{app.appId}</div>
                    <Chart
                      series={[paid, unpaid]}
                      options={{ labels: ['Paid', 'Not paid'], colors: ['#22C55E', '#EF4444'], chart: { type: 'donut' } }}
                      type="donut"
                      width={200}
                      height={150}
                    />
                  </Card>
                );
              })}
            </div>
          </>
        ) : noInstallmentData ? (
          <div className="flex flex-col min-h-[350px] bg-white rounded-xl shadow p-8">
            {/* Back Button */}
            <div className="gap-3 flex items-center">
              <div
                className="bg-[#F3EDDD] rounded-lg hover:bg-gray-100 transition duration-300 flex items-center justify-center w-10 h-10"
                onClick={navigateBack}
              >
                {isRtl ? (
                  <ArrowRightOutlined className="text-black text-lg font-extrabold" />
                ) : (
                  <ArrowLeftOutlined className="text-black text-lg font-extrabold" />
                )}
              </div>
              <div className="text-[#161616] text-[22px] font-normal font-sans">
                {translate("SETTINGS.BACK")}
              </div>
            </div>
            {/* Empty State */}
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div className="flex flex-col items-center">
                  <span className="text-gray-700 text-lg font-semibold mb-2">
                    {translate("SETTINGS.INSTALLMENT_DATA_UNAVAILABLE") || "Installment data is unavailable"}
                  </span>
                  <span className="text-gray-500 text-base">
                    {translate("SETTINGS.INSTALLMENT_DATA_UNAVAILABLE_DESC") ||
                      "There are currently no installment records available for this application."}
                  </span>
                </div>
              }
            />
            <div className="mt-8 flex flex-col items-center">
              <svg width="64" height="64" fill="none" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="32" fill="#F3EDDD" />
                <path d="M32 20v12M32 44h.02M24 32a8 8 0 1 1 16 0 8 8 0 0 1-16 0Z" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-[#D97706] font-medium mt-3">
                {translate("SETTINGS.CHECK_BACK_LATER") || "Please check back later or contact support if you believe this is an error."}
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-xl min-h-16 mb-4 px-4 py-8 bg-white items-center shadow-[0_4px_10px_-2px_rgba(0,0,0,0.1),0_-4px_10px_-2px_rgba(0,0,0,0.05)]">
              <div className="gap-3 flex items-center">
                <div
                  className="bg-[#F3EDDD] rounded-lg hover:bg-gray-100 transition duration-300 flex items-center justify-center w-10 h-10"
                  onClick={navigateBack}
                >
                  {isRtl ? (
                    <ArrowRightOutlined className="text-black text-lg font-extrabold" />
                  ) : (
                    <ArrowLeftOutlined className="text-black text-lg font-extrabold" />
                  )}
                </div>
                <div className="text-[#161616] text-[22px] font-normal font-sans">
                  {translate("SETTINGS.BACK")}
                </div>
              </div>

              <div className="text-black text-2xl font-semibold mt-3 mb-3">
                {translate("SETTINGS.INSTLMNT_DASHBOADRD_SUMMARY")}
              </div>

              {/* Responsive Container */}
              <div className="mb-5 rounded-[20px] px-5">
                <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-8 mb-8">
                  {/* Donut Chart */}
                  <div className="w-80 h-70 flex items-center justify-center">
                    <Chart
                      options={chartData.options}
                      series={chartData.series}
                      type="donut"
                      width="350"
                      height="250"
                    />
                  </div>

                  {/* Legend */}
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full bg-green-500"></div>
                      <span className="text-gray-700 font-medium">
                        {`Paid: ${paidAmount.toLocaleString()}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full bg-red-500"></div>
                      <span className="text-gray-700 font-medium">
                        {`Not paid: ${unpaidAmount.toLocaleString()}`}
                      </span>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  {translate("SETTINGS.INSTALLMENT_DETAILS") || "Installment Details"}
                </h3>

                {/* Table for larger screens */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full border-collapse rounded-lg">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          {translate("SETTINGS.INSTALLMENT_NUMBER") || "Installment Number"}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          {translate("SETTINGS.INSTALLMENT_DUE_DATE") || "Due Date"}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          {translate("SETTINGS.INSTALLMENT_AMOUNT") || "Installment Amount"}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          {translate("SETTINGS.INSTALLMENT_STATUS") || "Status"}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {installmentData.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 border-b border-gray-200">
                          <td className="px-4 py-3 text-gray-800">
                            {item.installmentNumber}
                          </td>
                          <td className="px-4 py-3 text-gray-800">
                            {item.dueDate}
                          </td>
                          <td className="px-4 py-3 text-gray-800">
                            {item.installmentAmount}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex px-6 py-1 text-xs font-semibold rounded-md ${item.status === 'Paid'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}
                            >
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Cards for mobile screens */}
                <div className="md:hidden space-y-4">
                  {installmentData.map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                      <div className="flex flex-col space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-900 font-semibold">
                            {translate("SETTINGS.INSTALLMENT_NUMBER") || "Installment Number"}:
                          </span>
                          <span className="text-gray-900 font-semibold">
                            {item.installmentNumber}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600 text-sm">
                            {translate("SETTINGS.INSTALLMENT_DUE_DATE") || "Due Date"}:
                          </span>
                          <span className="text-gray-900 font-medium">
                            {item.dueDate}
                          </span>
                        </div>

                        <div className="flex flex-row justify-between items-center">
                          <div className="flex items-center">
                            <img
                              src={SaudiRiyal}
                              alt="Tamweel Logo"
                              className="h-5 w-5 mr-2"
                            />
                            <span className="text-gray-900 font-medium">
                              {item.installmentAmount.toLocaleString()}
                              <span className="text-gray-600 text-sm ml-1">amount</span>
                            </span>
                          </div>
                          <span
                            className={`px-3 py-1 text-xs font-medium rounded-md ${item.status === 'Paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                              }`}
                          >
                            {item.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
              </div>
            </div>
            <p className="text-center text-gray-500 text-sm mt-4">
              {translate("SETTINGS.ALL_RIGHTS_RESERVED") || "All Right Reserved"}
            </p>
          </>
        )}
    </>
  );
}

export default InstlmntDashboard;
